import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../store/axiosInterceptor';
import { useToast } from '../Toast/ToastContext';

interface AssignedCoach {
  id: string;
  name: string;
  avatar?: string;
  area_of_expertise?: string;
}

interface Coach {
  id: string;
  name: string;
  avatar?: string;
  area_of_expertise?: string;
  approved?: boolean;
  status?: string;
}

export interface Student {
  id: string;
  name: string;
  email: string;
  status: 'Ongoing' | 'Not Started' | 'On Break' | 'Completed' | 'Unassigned';
  focusArea: string;
  completedSessions: number;
  lastSession: string;
  coach_id?: string;
  assigned_coach?: AssignedCoach;
}

interface StudentsTableProps {
  students: Student[];
  onAddStudent: () => void;
  onViewProfile: (student: Student) => void;
  onUpdateTarget: (student: Student) => void;
  onMessage: (student: Student) => void;
  onSearch: (query: string) => void;
  onAssignCoach?: (student: Student) => void;
  onChangeCoach?: (student: Student) => void;
  onRefresh?: () => void;
}

const StudentsTable: React.FC<StudentsTableProps> = ({
  students,
  onAddStudent,
  onViewProfile,
  onUpdateTarget,
  onMessage,
  onSearch,
  onAssignCoach,
  onChangeCoach,
  onRefresh,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedCoachId, setSelectedCoachId] = useState<string>('');
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loadingCoaches, setLoadingCoaches] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch(query);
  };

  const getStatusColor = (status: Student['status']) => {
    switch (status) {
      case 'Ongoing':
        return 'bg-green-50 text-green-700 border border-green-200';
      case 'Not Started':
        return 'bg-red-50 text-red-700 border border-red-200';
      case 'On Break':
        return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
      case 'Completed':
        return 'bg-gray-50 text-gray-700 border border-gray-200';
      case 'Unassigned':
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      default:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const fetchCoaches = async () => {
    try {
      setLoadingCoaches(true);
      const response = await axiosInstance.get<Coach[]>('/api/coaches');
      // Show all coaches
      setCoaches(response.data || []);
    } catch (error: any) {
      showToast('Failed to fetch coaches', 'error');
      setCoaches([]);
    } finally {
      setLoadingCoaches(false);
    }
  };

  const handleOpenAssignModal = (student: Student) => {
    setSelectedStudent(student);
    setSelectedCoachId('');
    setShowAssignModal(true);
    fetchCoaches();
    setOpenMenuId(null);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedStudent(null);
    setSelectedCoachId('');
  };

  const handleConfirmAssign = async () => {
    if (!selectedStudent || !selectedCoachId) {
      showToast('Please select a coach', 'info');
      return;
    }

    try {
      await axiosInstance.put(`/api/admin/students/${selectedStudent.id}/coach`, {
        coach_id: selectedCoachId,
      });

      showToast('Coach assigned successfully', 'success');
      handleCloseAssignModal();
      
      // Refresh the student list
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Failed to assign coach', 'error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
     

      {/* Search and Add Button */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative w-80">
          <input
            type="text"
            placeholder="Search Students"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-gray-300 focus:outline-none focus:ring-0"
          />
          <svg
            className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <button
          onClick={onAddStudent}
          className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Add Student
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#F2F4F7] bg-white shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Student</th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Status</th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                Focus Area
              </th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Coach</th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                Completed Sessions
              </th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Last Session</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-16">
                  <div className="text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                    <p className="mt-4 text-sm text-gray-500">No students found</p>
                  </div>
                </td>
              </tr>
            ) : 
              students.map((student) => (
              <tr key={student.id} className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors">
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 flex-shrink-0 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                      {getInitials(student.name)}
                    </div>
                    <div>
                      <button
                        onClick={() => onViewProfile(student)}
                        className="font-medium text-gray-900 hover:text-blue-600 transition-colors text-left"
                      >
                        {student.name}
                      </button>
                      <div className="text-xs text-gray-500">{student.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(
                      student.status
                    )}`}
                  >
                    {student.status}
                  </span>
                </td>
                <td className="px-5 py-4 text-sm text-gray-900">{student.focusArea}</td>                <td className="px-5 py-4">
                  {student.assigned_coach ? (
                    <div className="flex items-center gap-3">
                      {student.assigned_coach.avatar ? (
                        <img
                          src={student.assigned_coach.avatar}
                          alt={student.assigned_coach.name}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white text-xs font-semibold">
                          {student.assigned_coach.name.charAt(0)}
                        </div>
                      )}
                      <span className="text-sm text-gray-900">{student.assigned_coach.name}</span>
                    </div>
                  ) : !student.assigned_coach && (student.status.toLowerCase() === 'active' || student.status.toLowerCase() === 'pending') ? (
                    <button
                      onClick={() => handleOpenAssignModal(student)}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Assign Coach
                    </button>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>                <td className="px-5 py-4 text-sm text-gray-900">{student.completedSessions}</td>
                <td className="px-5 py-4 text-sm text-gray-500">{student.lastSession}</td>
                <td className="px-5 py-4">
                  <div className="relative" ref={openMenuId === student.id ? menuRef : null}>
                    <button 
                      className="rounded p-1 hover:bg-gray-100 transition-colors"
                      onClick={() => {
                        setOpenMenuId(openMenuId === student.id ? null : student.id);
                      }}
                    >
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                      </svg>
                    </button>
                    {openMenuId === student.id && (
                      <div className="absolute right-0 top-full z-10 mt-1 whitespace-nowrap rounded-lg border border-gray-100 bg-white py-1 shadow-lg min-w-[140px]">
                        <button
                          onClick={() => {
                            onViewProfile(student);
                            setOpenMenuId(null);
                          }}
                          className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          View Profile
                        </button>
                        {/* <button
                          onClick={() => {
                            onUpdateTarget(student);
                            setOpenMenuId(null);
                          }}
                          className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Update Target
                        </button> */}
                        <button
                          onClick={() => {
                            onMessage(student);
                            setOpenMenuId(null);
                          }}
                          className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Message
                        </button>
                        {student.assigned_coach && onChangeCoach && (
                          <button
                            onClick={() => {
                              onChangeCoach(student);
                              setOpenMenuId(null);
                            }}
                            className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Change Coach
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assign Coach Modal */}
      {showAssignModal && selectedStudent && (
        <div className="fixed -top-7 inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            {/* Modal Header */}
            <div className="flex flex-col items-center pt-8 pb-6 px-6 bg-white border-b border-gray-100">
              {selectedCoachId && coaches.find(c => c.id === selectedCoachId) ? (
                // Show selected coach details
                <>
                  <p className="text-xs text-blue-600 font-semibold uppercase mb-3">Selected Coach</p>
                  <div className="flex flex-col items-center">
                    {coaches.find(c => c.id === selectedCoachId)?.avatar ? (
                      <img
                        src={coaches.find(c => c.id === selectedCoachId)!.avatar}
                        alt={coaches.find(c => c.id === selectedCoachId)!.name}
                        className="h-16 w-16 rounded-full object-cover mb-3"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-semibold mb-3">
                        {coaches.find(c => c.id === selectedCoachId)!.name.charAt(0)}
                      </div>
                    )}
                    <h2 className="text-xl font-semibold text-gray-900">
                      {coaches.find(c => c.id === selectedCoachId)!.name}
                    </h2>
                    {coaches.find(c => c.id === selectedCoachId)?.area_of_expertise && (
                      <p className="text-sm text-gray-600 mt-1">
                        {coaches.find(c => c.id === selectedCoachId)!.area_of_expertise}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                // Show student info when no coach selected
                <>
                  <div className="h-14 w-14 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-lg font-semibold mb-4">
                    {getInitials(selectedStudent.name)}
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Assign Coach Manually</h2>
                  <p className="text-sm text-gray-500 mt-2">
                    Select a coach for <span className="font-medium text-gray-900">{selectedStudent.name}</span>
                  </p>
                </>
              )}
            </div>

            {/* Coaches List */}
            <div className="bg-gray-50 px-6 py-4 max-h-96 overflow-y-auto">
              {loadingCoaches ? (
                <div className="text-center py-8">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-gray-300 border-r-transparent"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading coaches...</p>
                </div>
              ) : coaches.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-sm text-gray-500">No coaches available</p>
                </div>
              ) : (
                coaches.map((coach) => (
                  <div
                    key={coach.id}
                    onClick={() => setSelectedCoachId(coach.id)}
                    className={`flex items-center justify-between p-3 rounded-lg mb-2 cursor-pointer transition-all bg-[#F2F4F7] ${
                      selectedCoachId === coach.id
                        ? 'bg-blue-50 border-2 border-blue-500 shadow-sm'
                        : 'bg-white hover:bg-gray-100 border border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      {coach.avatar ? (
                        <img
                          src={coach.avatar}
                          alt={coach.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
                          selectedCoachId === coach.id
                            ? 'bg-blue-600'
                            : 'bg-gradient-to-br from-blue-400 to-purple-500'
                        }`}>
                          {coach.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <p className={`text-sm font-medium leading-tight ${
                          selectedCoachId === coach.id ? 'text-blue-900' : 'text-gray-900'
                        }`}>
                          {coach.name}
                        </p>
                        {coach.area_of_expertise && (
                          <p className={`text-xs leading-tight ${
                            selectedCoachId === coach.id ? 'text-blue-700' : 'text-gray-500'
                          }`}>
                            {coach.area_of_expertise}
                          </p>
                        )}
                      </div>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                        selectedCoachId === coach.id
                          ? 'bg-blue-600 border-2 border-blue-600'
                          : 'border-2 border-gray-300'
                      }`}
                    >
                      {selectedCoachId === coach.id && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 px-6 py-4 bg-white border-t border-gray-200">
              <button
                onClick={handleConfirmAssign}
                disabled={!selectedCoachId || loadingCoaches}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  !selectedCoachId || loadingCoaches
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                Assign
              </button>
              <button
                onClick={handleCloseAssignModal}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsTable;
